/**
 * Dexie Database Setup
 *
 * IndexedDB wrapper using Dexie.js for type-safe, persistent storage.
 * Designed for easy migration to AWS Amplify DataStore.
 */

import Dexie, { type EntityTable } from 'dexie';
import type { Ticket, Tech, User } from '../types/ticket';
import type { Article, ArticleStep } from '../types/knowledgeBase';
import type { TicketActivity } from '../types/ticketActivity';
import type { Notification } from '../types/notification';

/**
 * Database schema definition
 * Extends Dexie with typed entity tables
 */
class PerceptaDatabase extends Dexie {
  tickets!: EntityTable<Ticket, 'id'>;
  techs!: EntityTable<Tech, 'id'>;
  users!: EntityTable<User, 'id'>;
  articles!: EntityTable<Article, 'id'>;
  activities!: EntityTable<TicketActivity, 'id'>;
  notifications!: EntityTable<Notification, 'id'>;

  constructor() {
    super('PerceptaServiceDesk');

    // Version 1: Original schema
    this.version(1).stores({
      tickets: 'id, ticketNumber, status, assignedTechId, priority, createdAt, location',
      techs: 'id, userId, tier, region, location, workStatus',
      users: 'id, email, role, status',
      articles: 'id, category, authorId, isPublished, updatedAt',
    });

    // Version 2: Add article templates and access control
    this.version(2)
      .stores({
        tickets: 'id, ticketNumber, status, assignedTechId, priority, createdAt, location',
        techs: 'id, userId, tier, region, location, workStatus',
        users: 'id, email, role, status, department',
        articles: 'id, category, authorId, isPublished, updatedAt, articleType, *audiences',
      })
      .upgrade(async (trans) => {
        // Migrate existing articles to new schema
        await trans
          .table('articles')
          .toCollection()
          .modify((article: Record<string, unknown>) => {
            // Set default article type
            article.articleType = 'how-to';

            // Set default audience (all departments)
            article.audiences = ['All Departments'];

            // Migrate steps to new content structure
            const steps = (article.steps as ArticleStep[]) || [];
            article.articleContent = {
              type: 'how-to',
              content: { steps },
            };
          });

        // Migrate existing users to have a department
        await trans
          .table('users')
          .toCollection()
          .modify((user: Record<string, unknown>) => {
            if (!user.department) {
              user.department = 'IT';
            }
          });
      });

    // Version 3: Add ticket types, categories, and requester info
    this.version(3)
      .stores({
        tickets:
          'id, ticketNumber, status, assignedTechId, priority, createdAt, location, ticketType, category, requesterId',
        techs: 'id, userId, tier, region, location, workStatus',
        users: 'id, email, role, status, department, name',
        articles: 'id, category, authorId, isPublished, updatedAt, articleType, *audiences',
      })
      .upgrade(async (trans) => {
        // Migrate existing tickets to new schema with ticket type, category, and requester
        await trans
          .table('tickets')
          .toCollection()
          .modify((ticket: Record<string, unknown>) => {
            // Set default ticket type to Incident
            if (!ticket.ticketType) {
              ticket.ticketType = 'Incident';
            }

            // Set default category and subcategory
            if (!ticket.category) {
              ticket.category = 'Other';
            }
            if (!ticket.subcategory) {
              ticket.subcategory = 'General';
            }

            // Set requester info from createdBy or defaults
            if (!ticket.requesterId) {
              ticket.requesterId = (ticket.createdBy as string) || 'unknown';
            }
            if (!ticket.requesterName) {
              ticket.requesterName = 'Legacy User';
            }
            if (!ticket.requesterEmail) {
              ticket.requesterEmail = 'unknown@legacy.com';
            }
            if (!ticket.requesterDepartment) {
              ticket.requesterDepartment = 'IT';
            }

            // Set creator info
            if (!ticket.createdBy) {
              ticket.createdBy = ticket.requesterId;
            }
            if (!ticket.createdByName) {
              ticket.createdByName = ticket.requesterName;
            }
          });
      });

    // Version 4: Add activities table and new status workflow
    this.version(4)
      .stores({
        tickets:
          'id, ticketNumber, status, assignedTechId, priority, createdAt, location, ticketType, category, requesterId, assignmentGroup, escalationTier',
        techs: 'id, userId, tier, region, location, workStatus',
        users: 'id, email, role, status, department, name',
        articles: 'id, category, authorId, isPublished, updatedAt, articleType, *audiences',
        activities: 'id, ticketId, type, visibility, createdAt, [ticketId+createdAt]',
      })
      .upgrade(async (trans) => {
        // Migrate existing tickets: consolidate ON_HOLD and WAITING_ON_USER to WAITING
        await trans
          .table('tickets')
          .toCollection()
          .modify((ticket: Record<string, unknown>) => {
            // Convert old statuses to new unified Waiting status
            if (ticket.status === 'On Hold') {
              ticket.status = 'Waiting';
              ticket.waitingReason = 'Other';
              ticket.waitingReasonNote = 'Migrated from On Hold status';
            } else if (ticket.status === 'Waiting on User') {
              ticket.status = 'Waiting';
              ticket.waitingReason = 'User';
            }

            // Set default assignment group and escalation tier
            if (!ticket.assignmentGroup) {
              ticket.assignmentGroup = 'Service Desk';
            }
            if (!ticket.escalationTier) {
              ticket.escalationTier = 'T1';
            }
          });
      });

    // Version 5: Add notifications table for in-app notification system
    this.version(5).stores({
      tickets:
        'id, ticketNumber, status, assignedTechId, priority, createdAt, location, ticketType, category, requesterId, assignmentGroup, escalationTier',
      techs: 'id, userId, tier, region, location, workStatus',
      users: 'id, email, role, status, department, name',
      articles: 'id, category, authorId, isPublished, updatedAt, articleType, *audiences',
      activities: 'id, ticketId, type, visibility, createdAt, [ticketId+createdAt]',
      notifications:
        'id, recipientId, recipientType, [recipientId+recipientType], [recipientId+recipientType+isRead], ticketId, activityId, type, groupKey, createdAt, [activityId+recipientId]',
    });

    // Version 6: Ensure required demo tech records exist
    // This migration handles existing databases that were created before
    // the demo techs were added to the seed data
    this.version(6)
      .stores({
        tickets:
          'id, ticketNumber, status, assignedTechId, priority, createdAt, location, ticketType, category, requesterId, assignmentGroup, escalationTier',
        techs: 'id, userId, tier, region, location, workStatus',
        users: 'id, email, role, status, department, name',
        articles: 'id, category, authorId, isPublished, updatedAt, articleType, *audiences',
        activities: 'id, ticketId, type, visibility, createdAt, [ticketId+createdAt]',
        notifications:
          'id, recipientId, recipientType, [recipientId+recipientType], [recipientId+recipientType+isRead], ticketId, activityId, type, groupKey, createdAt, [activityId+recipientId]',
      })
      .upgrade(async (trans) => {
        const now = new Date();

        // Required user records for demo/auth system
        const requiredUsers = [
          {
            id: 'user-daryn',
            email: 'Daryn.Paterson@Percepta.com',
            name: 'Daryn Paterson',
            role: 'T2 Tech',
            department: 'IT',
            status: 'Active',
            location: 'UK - Glasgow',
            employeeId: '4514501',
            cdsid: 'dpater11',
            clientLOB: 'Percepta Internal',
            lobProgram: 'G&A',
            lobSite: 'UK - Glasgow',
            createdAt: now,
            updatedAt: now,
          },
          {
            id: 'user-tech-2',
            email: 'marcus.johnson@percepta.com',
            name: 'Marcus Johnson',
            role: 'T1 Tech',
            department: 'IT',
            status: 'Active',
            location: 'US - Houston',
            employeeId: '4514502',
            cdsid: 'mjohns12',
            clientLOB: 'Percepta Internal',
            lobProgram: 'IT',
            lobSite: 'US - Houston',
            createdAt: now,
            updatedAt: now,
          },
          {
            id: 'user-tech-3',
            email: 'sarah.chen@percepta.com',
            name: 'Sarah Chen',
            role: 'T2 Tech',
            department: 'IT',
            status: 'Active',
            location: 'UK - Daventry',
            employeeId: '4514503',
            cdsid: 'schen99',
            clientLOB: 'Percepta Internal',
            lobProgram: 'IT',
            lobSite: 'UK - Daventry',
            createdAt: now,
            updatedAt: now,
          },
        ];

        // Required tech records for demo/auth system
        const requiredTechs = [
          {
            id: 'tech-daryn',
            name: 'Daryn Paterson',
            tier: 'T2',
            region: 'UK',
            location: 'UK - Glasgow',
            skills: ['Software', 'O365', 'Teams', 'VPN', 'Password Reset', 'MFA', 'Hardware'],
            currentLoad: 0,
            maxLoad: 5,
            isAvailable: true,
            userId: 'user-daryn',
            workStatus: 'Available',
          },
          {
            id: 'tech-2',
            name: 'Marcus Johnson',
            tier: 'T1',
            region: 'US',
            location: 'US - Houston',
            skills: ['Hardware', 'Laptop', 'Desktop', 'Password Reset', 'Account Setup'],
            currentLoad: 0,
            maxLoad: 5,
            isAvailable: true,
            userId: 'user-tech-2',
            workStatus: 'Available',
          },
          {
            id: 'tech-3',
            name: 'Sarah Chen',
            tier: 'T2',
            region: 'UK',
            location: 'UK - Daventry',
            skills: ['Software', 'Networking', 'VPN', 'Firewall', 'Access Request', 'UK Specific'],
            currentLoad: 0,
            maxLoad: 5,
            isAvailable: true,
            userId: 'user-tech-3',
            workStatus: 'Available',
          },
        ];

        const usersTable = trans.table('users');
        const techsTable = trans.table('techs');

        // Ensure required users exist
        for (const user of requiredUsers) {
          const existing = await usersTable.get(user.id);
          if (!existing) {
            await usersTable.add(user);
          }
        }

        // Ensure required techs exist
        for (const tech of requiredTechs) {
          const existing = await techsTable.get(tech.id);
          if (!existing) {
            await techsTable.add(tech);
          }
        }
      });

    // Version 7: Add assignmentGroup to techs for proper team-based reassignment
    this.version(7)
      .stores({
        tickets:
          'id, ticketNumber, status, assignedTechId, priority, createdAt, location, ticketType, category, requesterId, assignmentGroup, escalationTier',
        techs: 'id, userId, tier, region, location, workStatus, assignmentGroup',
        users: 'id, email, role, status, department, name',
        articles: 'id, category, authorId, isPublished, updatedAt, articleType, *audiences',
        activities: 'id, ticketId, type, visibility, createdAt, [ticketId+createdAt]',
        notifications:
          'id, recipientId, recipientType, [recipientId+recipientType], [recipientId+recipientType+isRead], ticketId, activityId, type, groupKey, createdAt, [activityId+recipientId]',
      })
      .upgrade(async (trans) => {
        const techsTable = trans.table('techs');

        // Update existing techs with assignment groups
        await techsTable.toCollection().modify((tech: Record<string, unknown>) => {
          // Assign groups based on tech ID or default to Service Desk
          if (!tech.assignmentGroup) {
            const techId = tech.id as string;
            if (techId === 'tech-3' || techId === 'tech-5') {
              tech.assignmentGroup = 'Networking';
            } else if (techId === 'tech-6') {
              tech.assignmentGroup = 'Systems';
            } else if (techId === 'tech-7') {
              tech.assignmentGroup = 'Security';
            } else {
              // Default: Service Desk (tech-daryn, tech-2, tech-4, etc.)
              tech.assignmentGroup = 'Service Desk';
            }
          }
        });

        // Add additional team members if they don't exist
        const additionalTechs = [
          {
            id: 'tech-4',
            name: 'Emily Rodriguez',
            tier: 'T1',
            region: 'US',
            location: 'US - Dearborn',
            skills: ['Password Reset', 'Account Setup', 'O365', 'Teams'],
            currentLoad: 2,
            maxLoad: 8,
            isAvailable: true,
            userId: 'user-tech-4',
            workStatus: 'Working',
            assignmentGroup: 'Service Desk',
          },
          {
            id: 'tech-5',
            name: 'James Wilson',
            tier: 'T2',
            region: 'US',
            location: 'US - Dearborn',
            skills: ['Networking', 'VPN', 'Firewall'],
            currentLoad: 1,
            maxLoad: 5,
            isAvailable: true,
            userId: 'user-tech-5',
            workStatus: 'Available',
            assignmentGroup: 'Networking',
          },
          {
            id: 'tech-6',
            name: 'Michael Torres',
            tier: 'T2',
            region: 'US',
            location: 'US - Houston',
            skills: ['Software', 'Desktop', 'Laptop'],
            currentLoad: 0,
            maxLoad: 6,
            isAvailable: true,
            userId: 'user-tech-6',
            workStatus: 'Available',
            assignmentGroup: 'Systems',
          },
          {
            id: 'tech-7',
            name: 'Lisa Anderson',
            tier: 'T2',
            region: 'US',
            location: 'US - Dearborn',
            skills: ['MFA', 'Access Request', 'Firewall'],
            currentLoad: 0,
            maxLoad: 4,
            isAvailable: true,
            userId: 'user-tech-7',
            workStatus: 'Available',
            assignmentGroup: 'Security',
          },
        ];

        for (const tech of additionalTechs) {
          const existing = await techsTable.get(tech.id);
          if (!existing) {
            await techsTable.add(tech);
          }
        }
      });

    // Version 8: Enhanced ticket type system with Problem and Change types
    // Adds support for:
    // - Request: approvalStatus, neededByDate
    // - Problem: problemStatement, rootCause, workaround, linkedIncidentIds
    // - Change: changeType, riskLevel, changeCategory, approvalStatus, rollbackPlan, implementationWindow
    this.version(8)
      .stores({
        tickets:
          'id, ticketNumber, status, assignedTechId, priority, createdAt, location, ticketType, category, requesterId, assignmentGroup, escalationTier, approvalStatus, changeType, riskLevel, changeCategory',
        techs: 'id, userId, tier, region, location, workStatus, assignmentGroup',
        users: 'id, email, role, status, department, name',
        articles: 'id, category, authorId, isPublished, updatedAt, articleType, *audiences',
        activities: 'id, ticketId, type, visibility, createdAt, [ticketId+createdAt]',
        notifications:
          'id, recipientId, recipientType, [recipientId+recipientType], [recipientId+recipientType+isRead], ticketId, activityId, type, groupKey, createdAt, [activityId+recipientId]',
      })
      .upgrade(async (trans) => {
        const ticketsTable = trans.table('tickets');

        // Migrate existing Request tickets to have approvalStatus
        await ticketsTable.toCollection().modify((ticket: Record<string, unknown>) => {
          if (ticket.ticketType === 'Request') {
            // Set default approval status for existing Request tickets
            if (!ticket.approvalStatus) {
              ticket.approvalStatus = 'Not Required';
            }
          }
        });
      });
  }
}

/**
 * Singleton database instance
 * Ensures single connection across the app
 */
export const db = new PerceptaDatabase();

/**
 * Database initialization state
 * Prevents multiple seed operations
 */
let isInitialized = false;

/**
 * Initialize the database with seed data if empty
 * Should be called once at app startup
 */
export async function initializeDatabase(seedFn: () => Promise<void>): Promise<void> {
  if (isInitialized) {
    return;
  }

  try {
    // Check if database has any data
    const ticketCount = await db.tickets.count();
    const techCount = await db.techs.count();
    const userCount = await db.users.count();

    // Seed only if completely empty
    if (ticketCount === 0 && techCount === 0 && userCount === 0) {
      await seedFn();
    }

    isInitialized = true;
  } catch (error) {
    console.error('Failed to initialize database:', error);
    throw error;
  }
}

/**
 * Clear all data from the database
 * Useful for testing or reset functionality
 */
export async function clearDatabase(): Promise<void> {
  await db.transaction(
    'rw',
    [db.tickets, db.techs, db.users, db.articles, db.activities, db.notifications],
    async () => {
      await db.tickets.clear();
      await db.techs.clear();
      await db.users.clear();
      await db.articles.clear();
      await db.activities.clear();
      await db.notifications.clear();
    }
  );
  isInitialized = false;
}

/**
 * Check if database is ready
 */
export function isDatabaseInitialized(): boolean {
  return isInitialized;
}
